import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Drawer, Modal, Typography, IconButton } from '@mui/material';
import CustomModal from '../../../../../common/components/Modal/Modal';
import { httpService } from '../../../../../utility/httpService';
import { httpConstants } from '../../../../../constants';
import { API_BASE, API_GRAPH_BASE } from '../../../../../config/env';
import { useGraphQLQuery } from '../../../../../customHooks/useGraphQL';
import { GET_AI_SOLUTIONS_DATA } from '../../../../../graphql/queries/commonQueries';
import Loader from '../../../../../common/components/Loader';
import ViewSolutionDetails from '../../../../SimulationXPLR/ViewSolutionDetails';
import ShowToast, { ToastTypeEnum } from '../../../../../common/components/ShowToast';
import {
  refreshTaxonomy,
  updateDiagramData,
} from '../../../../BpmnIoDiagram/store/diagramViewerSlice';
import { updateIdeationFieldState } from '../../../state';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import IdeationOpportunitiesCard from '../../../../../common/components/IdeationOpportunities/ideationAiOpportunitiesCard';
import IdeationMenu from '../../../../../common/components/IdeationMenu/IdeationMenu';
import ConfirmationModal from '../../../../../common/components/ConfirmationModal';
import { Close as CloseIcon } from '@mui/icons-material';
import { handleScrollToTop } from '../../../../SimulationXPLR/utils';
import { fetchAISolutionWithAgentEnablement } from '../../../../../utility/aiOpportunityUtils';
import type { Enhancement } from '../../../shared/types';
import { createIdeationVersion } from '../../NewIdeation/Versioning/VersioningManager';

// import { convertBpmnXmlToBase64 } from '../../../../../utility/reportUtils';
import DownloadProgressSnackbar from '../../../../../common/components/DownloadProgressSnackbar';
import { DOMAIN_OPTIONS } from '../../../shared/constants';

type OpportunityTaxonomy = {
  domain?: string;
  function?: string;
  process_group?: string;
  process?: string;
};

type BreadcrumbInput = {
  taxonomy?: OpportunityTaxonomy;
  fallbackDomain?: string;
  fallbackFunction?: string;
  fallbackProcessGroup?: string;
  fallbackProcess?: string;
  currentProcessName?: string;
};

const normalizeBreadcrumbSegment = (segment: string): string => {
  const normalizedSegment = segment.toLowerCase().replace(/\s/g, '');
  if (normalizedSegment === 'backoffice') return 'Back Office';
  if (normalizedSegment === 'middleoffice') return 'Middle Office';
  if (normalizedSegment === 'frontoffice') return 'Front Office';
  if (normalizedSegment === 'aiopportunities' || normalizedSegment === 'ai_opportunity') {
    return 'AI Opportunities';
  }

  return segment.charAt(0).toUpperCase() + segment.slice(1);
};

export const buildOpportunityBreadcrumbText = ({
  taxonomy,
  fallbackDomain,
  fallbackFunction,
  fallbackProcessGroup,
  fallbackProcess,
  currentProcessName,
}: BreadcrumbInput): string => {
  const domainValue = taxonomy?.domain || fallbackDomain;
  const domainLabel =
    DOMAIN_OPTIONS.find((option) => option.value === domainValue)?.label || domainValue;
  const breadcrumbSegments = [
    domainLabel,
    taxonomy?.function || fallbackFunction,
    taxonomy?.process_group || fallbackProcessGroup,
    currentProcessName || taxonomy?.process || fallbackProcess,
  ].filter((segment): segment is string => Boolean(segment && segment.trim()));

  return breadcrumbSegments.map(normalizeBreadcrumbSegment).join(' / ');
};

type ImplementationComplexity = {
  automation: 'low' | 'medium' | 'high';
  governance: 'low' | 'medium' | 'high';
  data_sources: 'low' | 'medium' | 'high';
  integrations: 'low' | 'medium' | 'high';
  recommendation: boolean;
  recommendation_rationale: string;
};

type AiSolutionRow = {
  id: string;
  serial: number;
  opportunity_name: string;
  impact?: string | null;
  roi?: string | null | React.ReactElement;
  complexity?: string | null | React.ReactElement;
  vault?: boolean;
  solution_enabler?: string | null;
  approval_status?: 'pending' | 'approved' | 'created' | null;
  description?: string | null;
  sequential_id?: string | null;
  implementation_complexity?: ImplementationComplexity | null;
};

interface AiOpportunitiesProps {
  diagramId: string;
  selectedCard?: any;
  refreshKey?: number;
  isLoading?: boolean;
  setActiveStep?: React.Dispatch<React.SetStateAction<string>>;
  enhancements?: Enhancement[];
  isProcess?: boolean;
  onMobileActionsChange?: (actions: MobileActionsState | null) => void;
  onVersionHistory?: () => void;
  isVersionViewMode?: boolean;
  previewOpportunityId?: string | null;
  onRestoreVersion?: () => void;
}

export type MobileActionsState = {
  hasActiveFilters: boolean;
  showMenu: boolean;
  disableDownload: boolean;
  onFilterClick: () => void;
  onEditClick: () => void;
  onDeleteClick: () => void;
  onDownloadClick: () => void;
};

const AiOpportunities: React.FC<AiOpportunitiesProps> = ({
  diagramId,
  selectedCard,
  refreshKey,
  isLoading = false,
  setActiveStep,
  // enhancements,
  isProcess = false,
  onMobileActionsChange,
  onVersionHistory,
  isVersionViewMode = false,
  previewOpportunityId,
  onRestoreVersion,
}) => {
  const currentOrg = localStorage.getItem('current_organization') || 'Empty';

  const [solutionData, setSolutionData] = useState<AiSolutionRow[]>([]);
  const [, setOriginalSolutionData] = useState<AiSolutionRow[]>([]);
  const [dataSourceType, setDataSourceType] = useState<'rest' | 'graphql' | null>(null);
  const [selectedSolutionFromLibrary, setSelectedSolutionFromLibrary] = useState<{
    id: string;
    name: string;
    vault?: boolean;
    solution_enabler?: string;
  }>({ id: '', name: '' });
  const [aiSolutionData, setAiSolutionData] = useState<any>(null);
  const [showVaultModal, setShowVaultModal] = useState<boolean>(false);
  const [enterpriseContextApplied, setEnterpriseContextApplied] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [solutionToDelete] = useState<string>('');
  const [internalLoading, setInternalLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const { selectedProcess } = useSelector((state: RootState) => state.newIdeation);

  const useCaseId = useSelector((state: RootState) => state.newIdeation.id);
  const selectedCardUseCaseId =
    selectedCard?.useCaseId || selectedCard?.use_case_id || selectedCard?.id;
  const combinedLoading = isLoading || internalLoading;
  const actualDiagramId = selectedCard?.diagramId || diagramId;
  const [showEditConfirmModal, setShowEditConfirmModal] = useState<boolean>(false);
  const [needsRefetchOnClose, setNeedsRefetchOnClose] = useState<boolean>(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState<boolean>(false);
  const [renameProcessName, setRenameProcessName] = useState<string>(selectedProcess || '');
  const [currentProcessName, setCurrentProcessName] = useState<string>(
    selectedCard?.name || selectedProcess || ''
  );
  const [loadingDocument, setLoadingDocument] = useState<boolean>(false);
  const [downloadProgressOpen, setDownloadProgressOpen] = useState<boolean>(false);
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [blockDeleteModalOpen, setBlockDeleteModalOpen] = useState<boolean>(false);
  const formState = useSelector((state: RootState) => state.newIdeation);

  const { data, loading: qlLoading } = useGraphQLQuery(
    GET_AI_SOLUTIONS_DATA,
    { v1: { subprocessId: `${selectedCard?.id}` }, v2: ['name'] },
    { skip: !selectedCard?.id }
  );

  const [primarySort] = useState<'roi' | 'complexity'>('roi');
  const [filterValues, setFilterValues] = useState({
    roi: { high: false, medium: false, low: false },
    complexity: { high: false, medium: false, low: false },
    type: { transformative: false, breakthrough: false, incremental: false },
  });
  // Track applied filters separately from temporary UI selections
  const [appliedFilters, setAppliedFilters] = useState({
    roi: { high: false, medium: false, low: false },
    complexity: { high: false, medium: false, low: false },
    type: { transformative: false, breakthrough: false, incremental: false },
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [filteredData, setFilteredData] = useState<AiSolutionRow[]>([]);
  const { diagramData } = useSelector((state: RootState) => state.diagramViewer);
  const { previewVersionData } = useSelector((state: RootState) => state.newIdeation);

  // Update the filter button onClick
  const handleOpenFilterDrawer = useCallback(() => setShowFilterDrawer(true), []);
  const handleCloseFilterDrawer = () => {
    setFilterValues(appliedFilters);
    setShowFilterDrawer(false);
  };

  const hasAnyTempFiltersSelected = () => {
    return Object.values(filterValues).some((category) => Object.values(category).some(Boolean));
  };

  // Add handlers for filter changes
  const handleFilterChange = (category: 'roi' | 'complexity' | 'type', option: string) => {
    setFilterValues((prev) => {
      if (category === 'roi' || category === 'complexity') {
        const key = option.toLowerCase() as 'high' | 'medium' | 'low';
        return {
          ...prev,
          [category]: {
            ...prev[category as 'roi' | 'complexity'],
            [key]: !prev[category][key],
          },
        };
      } else {
        const key = option.toLowerCase() as 'transformative' | 'breakthrough' | 'incremental';
        return {
          ...prev,
          type: {
            ...prev.type,
            [key]: !prev.type[key],
          },
        };
      }
    });
  };

  const handleClearAll = async () => {
    setFilterValues({
      roi: { high: false, medium: false, low: false },
      complexity: { high: false, medium: false, low: false },
      type: { transformative: false, breakthrough: false, incremental: false },
    });
    // Clear applied filters
    setAppliedFilters({
      roi: { high: false, medium: false, low: false },
      complexity: { high: false, medium: false, low: false },
      type: { transformative: false, breakthrough: false, incremental: false },
    });

    // Fetch all data without filters
    setInternalLoading(true);
    try {
      const response = await fetchAISolution();
      if (response?.results?.length > 0) {
        const mapped = response.results.map(
          (
            {
              id,
              opportunity_name,
              roi,
              complexity,
              impact,
              vault,
              solution_enabler,
              status,
              description,
              sequential_id,
              implementation_complexity,
            }: any,
            index: number
          ) => ({
            id,
            serial: index + 1,
            opportunity_name,
            roi: roi || '-',
            complexity: complexity || '-',
            impact: impact || '-',
            vault: vault || false,
            solution_enabler: solution_enabler || 'N/A',
            approval_status: status || null,
            description: description || null,
            sequential_id: sequential_id || '',
            implementation_complexity: implementation_complexity || {},
          })
        );

        setOriginalSolutionData(mapped);
        setFilteredData(mapped);
        sortSolutions(mapped);
        setDataSourceType('rest');
      } else {
        setOriginalSolutionData([]);
        setFilteredData([]);
        setSolutionData([]);
        setDataSourceType('rest');
      }
    } catch (error) {
      console.error('Error clearing filters:', error);
      ShowToast({ message: 'Error clearing filters', type: ToastTypeEnum.Error });
      setSolutionData([]);
    } finally {
      setInternalLoading(false);
    }
  };

  const handleAddFilter = async () => {
    // Save current filter values as applied filters
    setAppliedFilters(filterValues);

    setInternalLoading(true);
    try {
      // Fetch data with applied filters
      const response = await fetchAISolution(filterValues);
      if (response?.results?.length > 0) {
        const mapped = response.results.map(
          (
            {
              id,
              opportunity_name,
              roi,
              complexity,
              impact,
              vault,
              solution_enabler,
              status,
              description,
              sequential_id,
              implementation_complexity,
            }: any,
            index: number
          ) => ({
            id,
            serial: index + 1,
            opportunity_name,
            roi: roi || '-',
            complexity: complexity || '-',
            impact: impact || '-',
            vault: vault || false,
            solution_enabler: solution_enabler || 'N/A',
            approval_status: status || null,
            description: description || null,
            sequential_id: sequential_id || '',
            implementation_complexity: implementation_complexity || {},
          })
        );

        setOriginalSolutionData(mapped);
        setFilteredData(mapped);
        sortSolutions(mapped);
        setDataSourceType('rest');
      } else {
        setOriginalSolutionData([]);
        setFilteredData([]);
        setSolutionData([]);
        setDataSourceType('rest');
      }
    } catch (error) {
      console.error('Error applying filters:', error);
      ShowToast({ message: 'Error applying filters', type: ToastTypeEnum.Error });
      setSolutionData([]);
    } finally {
      setInternalLoading(false);
      setShowFilterDrawer(false);
    }
  };

  const extractTextFromReactNode = (node: any): string => {
    if (node == null) return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (typeof node === 'boolean') return '';
    if (node.props && node.props.children) {
      const children = node.props.children;
      if (Array.isArray(children)) return children.map(extractTextFromReactNode).join(' ');
      return extractTextFromReactNode(children);
    }
    if (Array.isArray(node)) return node.map(extractTextFromReactNode).join(' ');
    try {
      return String(node);
    } catch {
      return '';
    }
  };

  const normalizeValue = (val: any): string => {
    if (!val && val !== 0) return '';
    if (typeof val === 'string') return val.trim().toUpperCase();
    // handle JSX nodes which you sometimes store in solutionData
    const text = extractTextFromReactNode(val);
    return text.trim().toUpperCase();
  };

  // Priorities (base)
  const ROI_PRIORITY_BASE = ['HIGH', 'MEDIUM', 'LOW'];
  const COMPLEXITY_PRIORITY_BASE = ['LOW', 'MEDIUM', 'HIGH'];

  const buildPriorityOrder = () => {
    // primary = 'roi' => primaryOrder = ROI_PRIORITY_BASE (default High->Low)
    // but reversePrimary flips the primary order
    // secondary must follow the spec:
    // - if ROI primary, secondary complexity order: Low -> Medium -> High
    // - if Complexity primary, secondary ROI order: High -> Medium -> Low
    let roiOrder = [...ROI_PRIORITY_BASE];
    let complexityOrder = [...COMPLEXITY_PRIORITY_BASE];

    if (primarySort === 'roi') {
      roiOrder = [...ROI_PRIORITY_BASE]; // base
      // ensure ROI inside complexity default is High -> Low (as per spec)
      roiOrder = ['HIGH', 'MEDIUM', 'LOW'];
    }

    return { roiOrder, complexityOrder };
  };

  // Main sorting function used to sort and then set solutionData
  const sortSolutions = (rows: AiSolutionRow[]) => {
    const { roiOrder, complexityOrder } = buildPriorityOrder();

    const roiIndex = (v: string) => {
      const idx = roiOrder.indexOf(v);
      return idx === -1 ? roiOrder.length + 1 : idx;
    };
    const complexityIndex = (v: string) => {
      const idx = complexityOrder.indexOf(v);
      return idx === -1 ? complexityOrder.length + 1 : idx;
    };

    const sorted = [...rows].sort((a, b) => {
      const aRecommended = a.implementation_complexity?.recommendation ?? false;
      const bRecommended = b.implementation_complexity?.recommendation ?? false;

      if (aRecommended && !bRecommended) return -1;
      if (!aRecommended && bRecommended) return 1;

      const aRoi = normalizeValue(a.roi || '');
      const bRoi = normalizeValue(b.roi || '');
      const aComplexity = normalizeValue(a.complexity || '');
      const bComplexity = normalizeValue(b.complexity || '');

      if (primarySort === 'roi') {
        // Compare ROI by roiOrder
        const cmpRoi = roiIndex(aRoi) - roiIndex(bRoi);
        if (cmpRoi !== 0) return cmpRoi;
        // same ROI -> compare complexity by complexityOrder
        const cmpComplex = complexityIndex(aComplexity) - complexityIndex(bComplexity);
        if (cmpComplex !== 0) return cmpComplex;
      } else {
        // primary = complexity
        const cmpComplex = complexityIndex(aComplexity) - complexityIndex(bComplexity);
        if (cmpComplex !== 0) return cmpComplex;
        // same complexity -> compare roi by roiOrder
        const cmpRoi = roiIndex(aRoi) - roiIndex(bRoi);
        if (cmpRoi !== 0) return cmpRoi;
      }

      // final tie-breaker: alphabetical by opportunity_name
      const nameA = String(a.opportunity_name || '').toLowerCase();
      const nameB = String(b.opportunity_name || '').toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

    setSolutionData(sorted);
  };

  const fetchAISolution = useCallback(
    async (filters?: any): Promise<any> => {
      try {
        let url = `${API_BASE}/api/v1/e2e_taxonomy/opportunities/?diagram_id=${actualDiagramId}`;
        const queryParams: string[] = [];

        if (filters) {
          // Add ROI filters - convert to uppercase as per API expectations
          const roiFilters = Object.keys(filters.roi)
            .filter((key) => filters.roi[key])
            .map((key) => key.toUpperCase());
          if (roiFilters.length > 0) {
            queryParams.push(`roi=${roiFilters.join(',')}`);
          }

          // Add Complexity filters - convert to uppercase as per API expectations
          const complexityFilters = Object.keys(filters.complexity)
            .filter((key) => filters.complexity[key])
            .map((key) => key.toUpperCase());
          if (complexityFilters.length > 0) {
            queryParams.push(`complexity=${complexityFilters.join(',')}`);
          }
          const impactFilters = Object.keys(filters.type)
            .filter((key) => filters.type[key])
            .map((key) => {
              if (key === 'transformative') return 'Transformative';
              if (key === 'breakthrough') return 'Breakthrough';
              if (key === 'incremental') return 'Incremental';
              return key;
            });
          if (impactFilters.length > 0) {
            queryParams.push(`impactType=${impactFilters.join(',')}`);
          }
        }

        if (queryParams.length > 0) {
          url += `&${queryParams.join('&')}`;
        }

        console.log('API URL:', url);

        // Send GET request without body
        const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url);

        return response;
      } catch (err) {
        return null;
      }
    },
    [actualDiagramId]
  );

  const patchSolution = async (data: any) => {
    const payload = {
      roi: data.roi,
      ai_enablement: data?.agent?.ai_enablement ?? {},
      impact_value: data.impact,
      complexity: data.complexity,
      roi_description: data.roi_description,
      complexity_description: data.complexity_description,
      description: data.description,
      description_llm: data.description_llm,
      llm_agent: data.llm_agent || [],
      solution_enabler: data.solution_enabler,
      benefits: data.benefits,
      implementation_complexity: data.implementation_complexity,
      data_sources: data.data_sources,
      key_requirements: data.key_requirements || [],
      diagram_id: data.diagram_id,
      embedded: data.agent?.embedded || [],
      ai_solution: data.agent?.ai_solution || [],
      organization_id: currentOrg,
      solution_id: selectedSolutionFromLibrary.id.toString(),
      name: data.opportunity_name || '',
      user_updated_status: true,
    };

    if (!payload?.ai_enablement || Object.keys(payload?.ai_enablement ?? {})?.length === 0) {
      delete payload.ai_enablement;
    }
    if (!payload.embedded || payload.embedded.length === 0) {
      delete payload.embedded;
    } else if (
      typeof payload?.embedded === 'object' ||
      Object.keys(payload.embedded || {}).length === 0
    ) {
      delete payload.embedded;
    }

    const url = `${API_GRAPH_BASE}/custom-process/ai-opportunity?organization_id=${currentOrg}&solution_id=${selectedSolutionFromLibrary.id}`;
    const response = await httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, payload);

    const hasAppliedFilters = Object.values(appliedFilters).some((category) =>
      Object.values(category).some(Boolean)
    );
    await fetchAISolution(hasAppliedFilters ? appliedFilters : undefined);

    return response;
  };

  const deleteSolution = async (id: string) => {
    try {
      const url = `${API_GRAPH_BASE}/custom-process/ai-opportunity?organization_id=${currentOrg}&solution_id=${id}`;
      const response = await httpService<any>(httpConstants.METHOD_TYPE.DELETE, url);

      setSolutionData((prevData) => prevData.filter((solution) => solution.id !== id));

      if (selectedSolutionFromLibrary.id === id) {
        setSelectedSolutionFromLibrary({ id: '', name: '' });
        setAiSolutionData(null);
      }

      setShowDeleteModal(false);

      dispatch(refreshTaxonomy(true));

      const hasAppliedFilters = Object.values(appliedFilters).some((category) =>
        Object.values(category).some(Boolean)
      );
      await fetchAISolution(hasAppliedFilters ? appliedFilters : undefined);

      ShowToast({ message: 'Solution deleted successfully', type: ToastTypeEnum.Success });

      return response;
    } catch (error) {
      console.error('Error deleting solution:', error);
      ShowToast({ message: 'Error deleting solution', type: ToastTypeEnum.Error });
      setShowDeleteModal(false);
    }
  };

  const loadSolutions = async () => {
    setInternalLoading(true);
    try {
      const hasAppliedFilters = Object.values(appliedFilters).some((category) =>
        Object.values(category).some(Boolean)
      );
      const response = await fetchAISolution(hasAppliedFilters ? appliedFilters : undefined);

      if (response?.results?.length > 0) {
        const mapped = response.results.map(
          (
            {
              id,
              opportunity_name,
              roi,
              complexity,
              impact,
              vault,
              solution_enabler,
              status,
              description,
              sequential_id,
              implementation_complexity,
            }: any,
            index: number
          ) => ({
            id,
            serial: index + 1,
            opportunity_name,
            roi: roi || '-',
            complexity: complexity || '-',
            impact: impact || '-',
            vault: vault || false,
            solution_enabler: solution_enabler || 'N/A',
            approval_status: status || null,
            description: description || null,
            sequential_id: sequential_id || '',
            implementation_complexity: implementation_complexity || {},
          })
        );
        setOriginalSolutionData(mapped);
        setFilteredData(mapped);
        sortSolutions(mapped);
        setDataSourceType('rest');
        setInternalLoading(false);
        return;
      }
      if (hasAppliedFilters) {
        setSolutionData([]);
        setDataSourceType('rest');
        setInternalLoading(false);
        return;
      }
      if (data?.aiSolution?.fetch?.length) {
        const mapped = data.aiSolution.fetch.map(
          ({ id, name, impactAssessment }: any, index: number) => ({
            id,
            serial: index + 1,
            opportunity_name: name,
            impact: impactAssessment?.general ?? 'N/A',
          })
        );
        setOriginalSolutionData(mapped);
        setFilteredData(mapped);
        sortSolutions(mapped);
        setDataSourceType('graphql');
        setInternalLoading(false);
      } else if (!qlLoading) {
        setSolutionData([]);
        setDataSourceType(null);
        setInternalLoading(false);
      }
    } catch (error) {
      console.error('Error loading solutions:', error);
      ShowToast({ message: 'Error loading solutions', type: ToastTypeEnum.Error });
      setSolutionData([]);
      setDataSourceType(null);
      setInternalLoading(false);
    }
  };

  const hasAnyFiltersApplied = () => {
    return Object.values(appliedFilters).some((category) => Object.values(category).some(Boolean));
  };

  useEffect(() => {
    if (isVersionViewMode && previewVersionData?.ai_opportunities) {
      const mapped = previewVersionData.ai_opportunities.map((opp: any, index: number) => ({
        ...opp,
        id: opp.id,
        serial: index + 1,
        opportunity_name: opp.opportunity_name,
        roi: opp.roi || '-',
        complexity: opp.complexity || '-',
        impact: opp.impact || '-',
        vault: opp.vault || false,
        solution_enabler: opp.solution_enabler || 'N/A',
        approval_status: opp.status || null,
        description: opp.description || null,
        sequential_id: opp.sequential_id || '',
        implementation_complexity: opp.implementation_complexity || {},
      }));
      setOriginalSolutionData(mapped);
      setFilteredData(mapped);
      sortSolutions(mapped);
      setDataSourceType('rest');
      setInternalLoading(false);
    } else {
      loadSolutions();
    }
  }, [
    fetchAISolution,
    data,
    qlLoading,
    refreshKey,
    actualDiagramId,
    isVersionViewMode,
    previewVersionData,
  ]);

  // Handle auto-selection of preview opportunity
  useEffect(() => {
    if (previewOpportunityId && solutionData.length > 0) {
      const solution = solutionData.find((s) => String(s.id) === String(previewOpportunityId));
      if (solution) {
        setSelectedSolutionFromLibrary({
          id: solution.id,
          name: solution.opportunity_name,
          vault: solution.vault,
          solution_enabler: solution.solution_enabler || undefined,
        });
        setAiSolutionData({
          ...solution,
          id: solution.id,
          opportunity_name: solution.opportunity_name,
          roi: solution.roi,
          impact: solution.impact,
          complexity: solution.complexity,
          description: solution.description,
          roi_description: (solution as any).roi_description || '',
          complexity_description: (solution as any).complexity_description || '',
          benefits: (solution as any).benefits || {},
          data_sources: (solution as any).data_sources || {
            internal: { structured: [], unstructured: [] },
            external: { structured: [], unstructured: [] },
          },
          key_requirements: (solution as any).key_requirements || [],
          llm_agent: (solution as any).llm_agent || [],
          solution_enabler: solution.solution_enabler,
          diagram_id: actualDiagramId,
          organization_id: currentOrg,
          status: solution.approval_status || null,
        });
        setDataSourceType('rest');
      }
    }
  }, [previewOpportunityId, solutionData, actualDiagramId, currentOrg]);

  // Re-sort filteredData when primarySort changes or filteredData updates
  useEffect(() => {
    if (filteredData.length === 0) return;
    sortSolutions(filteredData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primarySort, filteredData]);

  const handleVaultChange = async (id: string, state: boolean) => {
    const url = `${API_BASE}/api/v1/e2e_taxonomy/new-vault/`;
    try {
      const response = await httpService<any>(
        state ? httpConstants.METHOD_TYPE.POST : httpConstants.METHOD_TYPE.DELETE,
        url,
        {
          organization_id: currentOrg,
          ai_solution_id: id,
        }
      );
      return !!response;
    } catch (error) {
      console.error('Vault update failed:', error);
      return false;
    }
  };

  const handleToggleVault = async (): Promise<boolean | void> => {
    if (!selectedSolutionFromLibrary || !selectedSolutionFromLibrary.id) return;

    const currentVault = selectedSolutionFromLibrary.vault ?? false;
    const newState = !currentVault;
    const success = await handleVaultChange(selectedSolutionFromLibrary.id, newState);

    if (success) {
      setSelectedSolutionFromLibrary({ ...selectedSolutionFromLibrary, vault: newState });

      setSolutionData((prevData) =>
        prevData.map((solution) =>
          solution.id === selectedSolutionFromLibrary.id
            ? { ...solution, vault: newState }
            : solution
        )
      );

      if (newState) {
        ShowToast({ message: 'Solution added to list', type: ToastTypeEnum.Success });
      } else {
        ShowToast({ message: 'Solution removed from list', type: ToastTypeEnum.Success });
      }
      setShowVaultModal(false);
      return newState;
    } else {
      ShowToast({ message: 'Unable to process request', type: ToastTypeEnum.Error });
      setShowVaultModal(false);
      return currentVault;
    }
  };

  const handleCloseVaultModal = () => setShowVaultModal(false);

  const handleSave = async (data: any) => {
    // NOTE: Do NOT call setAiSolutionData(data) here before the PATCH.
    // Doing so triggers the reactive hasFetchedEnterpriseTechRef reset effect in
    // ViewSolutionDetails which re-fires the cache-restore logic and overwrites
    // the correctly updated benefits with stale cached data.

    // Optimistically update the solution list (roi, complexity, etc.) for fast UI feedback
    setSolutionData((prevData) =>
      prevData.map((solution) =>
        solution.id === selectedSolutionFromLibrary.id
          ? {
              ...solution,
              serial: solution.serial,
              agent: data.agent,
              opportunity_name: data.opportunity_name || solution.opportunity_name,
              roi: data.roi ? String(data.roi).toUpperCase() : '-',
              complexity: data.complexity ? String(data.complexity).toUpperCase() : '-',
              impact: data.impact || solution.impact,
              solution_enabler: data.solution_enabler || solution.solution_enabler,
              approval_status: data.status || solution.approval_status,
              description: data.description || solution.description,
              vault: data.vault !== undefined ? data.vault : solution.vault,
            }
          : solution
      )
    );

    try {
      await patchSolution(data);

      const updatedSolutionData = await fetchAISolutionWithAgentEnablement({
        solutionId: selectedSolutionFromLibrary.id,
        diagramId: actualDiagramId,
        organizationId: currentOrg,
      });

      if (updatedSolutionData) {
        // Invalidate the global agent cache for this solution so that stale cached
        // data cannot be restored and override the freshly-fetched correct response.
        if ((window as any).agentDataCache) {
          (window as any).agentDataCache.delete(String(selectedSolutionFromLibrary.id));
        }
        if ((window as any).solutionSyncCache) {
          (window as any).solutionSyncCache.delete(String(selectedSolutionFromLibrary.id));
        }

        // Now set the correct fresh data from the API — this is the single source of truth.
        setAiSolutionData(updatedSolutionData);

        // Update solutionData with the fresh API response to ensure consistency
        setSolutionData((prevData) =>
          prevData.map((solution) =>
            solution.id === selectedSolutionFromLibrary.id
              ? {
                  ...solution,
                  roi: (updatedSolutionData as any)?.roi
                    ? String((updatedSolutionData as any).roi).toUpperCase()
                    : '-',
                  complexity: (updatedSolutionData as any)?.complexity
                    ? String((updatedSolutionData as any).complexity).toUpperCase()
                    : '-',
                  impact: (updatedSolutionData as any)?.impact || solution.impact,
                  approval_status: (updatedSolutionData as any)?.status || solution.approval_status,
                  description: (updatedSolutionData as any)?.description || solution.description,
                  vault:
                    (updatedSolutionData as any)?.vault !== undefined
                      ? (updatedSolutionData as any).vault
                      : solution.vault,
                }
              : solution
          )
        );
      }

      setAiSolutionData(updatedSolutionData);

      // Create version snapshot for AI Opportunity
      const ideationId = useCaseId || selectedCardUseCaseId;
      if (ideationId && !isVersionViewMode) {
        await createIdeationVersion(ideationId, 'manual', {
          step_name: 'ai_opportunity',
          opportunity_id: selectedSolutionFromLibrary.id || '',
        });
      }

      ShowToast({ message: 'Solution updated successfully', type: ToastTypeEnum.Success });
    } catch (error) {
      console.error('Error updating solution:', error);
      ShowToast({ message: 'Error updating solution', type: ToastTypeEnum.Error });
    }
  };

  const handleUpdateSolution = (data: any) => {
    setAiSolutionData(data);
    setSolutionData((prevData) =>
      prevData.map((solution) =>
        solution.id === selectedSolutionFromLibrary.id
          ? {
              ...solution,
              approval_status: data.status || solution.approval_status,
            }
          : solution
      )
    );
  };
  const handleUpdateRenameProcess = async (newName: string) => {
    try {
      const url = `${API_BASE}/api/v1/e2e_taxonomy/level2/${selectedCard?.id}/`;
      const payload = { name: newName };

      await httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, payload);
      setCurrentProcessName(newName);
      dispatch(updateIdeationFieldState({ field: 'selectedProcess', value: newName }));
      dispatch(
        updateDiagramData({
          selectedCard: {
            ...selectedCard,
            name: newName,
          },
        })
      );
      setRenameProcessName(newName);
      dispatch(refreshTaxonomy(true));
      try {
        dispatch(
          updateDiagramData({
            selectedCard: {
              ...(selectedCard || {}),
              name: newName,
              taxonomy: {
                ...((selectedCard && selectedCard.taxonomy) || {}),
                process: newName,
              },
            },
            taxonomySubprocessName: newName,
          })
        );
      } catch (err) {
        console.warn('Failed to update diagram viewer selectedCard name in redux', err);
      }

      // Create version snapshot to capture the rename
      const ideationId = useCaseId || selectedCardUseCaseId;
      if (ideationId && !isVersionViewMode) {
        await createIdeationVersion(ideationId, 'manual');
      }

      ShowToast({ message: 'Process name updated successfully', type: ToastTypeEnum.Success });
    } catch (error) {
      console.error('Error updating process name:', error);
      ShowToast({ message: 'Failed to update process name', type: ToastTypeEnum.Error });
    }
  };

  useEffect(() => {
    if (showEditConfirmModal) {
      setRenameProcessName(
        selectedCard?.opportunity?.items?.[0]?.opportunity_title ||
          selectedCard?.name ||
          selectedProcess ||
          ''
      );
    }
  }, [showEditConfirmModal]);

  const handleRemoveClick = async () => {
    try {
      if (aiSolutionData.status === 'created') {
        const url = `${API_BASE}/api/v1/e2e_taxonomy/opportunities/${aiSolutionData.id}/`;
        await httpService(httpConstants.METHOD_TYPE.DELETE, url);
        setSelectedSolutionFromLibrary({ id: '', name: '' });
      }
      ShowToast({
        type: ToastTypeEnum.Success,
        message: 'Process removed successfully',
      });
    } catch (error) {
      console.error('Error removing solution:', error);
      ShowToast({
        type: ToastTypeEnum.Error,
        message: 'Error removing process',
      });
    } finally {
      loadSolutions();
      handleScrollToTop();
    }
  };

  const handleRenameProcess = useCallback(() => {
    setRenameProcessName(selectedCard?.name || selectedProcess || '');
    setShowEditConfirmModal(true);
  }, [selectedCard?.name, selectedProcess]);

  const handleDeleteIdeation = useCallback(() => {
    const downstreamOpportunities = solutionData.filter(
      (s) => s.approval_status && s.approval_status !== 'created'
    );

    if (downstreamOpportunities.length > 0) {
      setBlockDeleteModalOpen(true); // show blocking modal
    } else {
      setShowDeleteConfirmModal(true); // existing confirmation flow
    }
  }, [solutionData]);

  const handleConfirmDeleteIdeation = async () => {
    try {
      const caseIdToUse = selectedCardUseCaseId || useCaseId;

      if (!caseIdToUse) {
        ShowToast({ message: 'Use case ID not found', type: ToastTypeEnum.Error });
        return;
      }

      const url = `${API_BASE}/api/v1/ideation/use-cases/${caseIdToUse}/delete/`;
      const response = await httpService<any>(httpConstants.METHOD_TYPE.DELETE, url);

      if (response) {
        ShowToast({ message: 'Ideation deleted successfully', type: ToastTypeEnum.Success });
        setShowDeleteConfirmModal(false);

        if (setActiveStep) {
          setActiveStep('dashboard');
        }
      } else {
        ShowToast({ message: 'Failed to delete ideation', type: ToastTypeEnum.Error });
      }
    } catch (error) {
      console.error('Error deleting ideation:', error);
      ShowToast({ message: 'Error deleting ideation', type: ToastTypeEnum.Error });
      setShowDeleteConfirmModal(false);
    }
  };

  // useEffect(() => {
  //   if (!state?.cameFromDashboardDetails) return;
  //   if (!solutionData || solutionData.length === 0) return;

  //   const filteredSolution = solutionData.find((item) => item.id === state.opportunity.id);

  //   if (!filteredSolution) return;

  //   setSelectedSolutionFromLibrary({
  //     id: filteredSolution.id,
  //     name: filteredSolution.opportunity_name,
  //     vault: filteredSolution.vault,
  //     solution_enabler: filteredSolution.solution_enabler || undefined,
  //   });

  //   setAiSolutionData({
  //     id: filteredSolution.id,
  //     opportunity_name: filteredSolution.opportunity_name,
  //     roi: filteredSolution.roi,
  //     impact: filteredSolution.impact,
  //     complexity: filteredSolution.complexity,
  //     description: filteredSolution.description,
  //     roi_description: '',
  //     complexity_description: '',
  //     benefits: {},
  //     data_sources: {
  //       internal: { structured: [], unstructured: [] },
  //       external: { structured: [], unstructured: [] },
  //     },
  //     key_requirements: [],
  //     llm_agent: [],
  //     solution_enabler: filteredSolution.solution_enabler,
  //     diagram_id: actualDiagramId,
  //     organization_id: currentOrg,
  //     status: filteredSolution.approval_status || null,
  //   });

  //   setDataSourceType('rest');
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [state, solutionData]);

  // const processReportData = async () => {
  //   try {
  //     const caseIdToUse = selectedCardUseCaseId || useCaseId;

  //     if (!caseIdToUse) {
  //       ShowToast({ message: 'Use case ID not found', type: ToastTypeEnum.Error });
  //       return;
  //     }

  //     const ideationDetails = await httpService<any>(
  //       httpConstants.METHOD_TYPE.GET,
  //       `${API_BASE}/api/v1/ideation/use-cases/${caseIdToUse}/`
  //     );

  //     const proposedEnhancementsFromApiRaw = ideationDetails?.proposed_enhancements?.items ?? [];

  //     const proposedEnhancements: Enhancement[] = Array.isArray(proposedEnhancementsFromApiRaw)
  //       ? proposedEnhancementsFromApiRaw.map((item: any, index: number) => ({
  //           id: item.id ?? String(index),
  //           enhancement_title: item.opportunity_title || item.title || 'AI Enhancement Opportunity',
  //           enhancement_description:
  //             item.opportunity_description || item.description || 'Description not available',
  //         }))
  //       : [];

  //     // Get diagram ID from diagramData
  //     const diagramId =
  //       (diagramData as any)?.diagram?.id ||
  //       (diagramData as any)?.DiagramData?.diagram?.id ||
  //       actualDiagramId;

  //     // Fetch detailed solution data for all AI opportunities using diagram_id
  //     const opportunitiesIDsResponse = await httpService<{ results: any[] }>(
  //       httpConstants.METHOD_TYPE.GET,
  //       `${API_BASE}/api/v1/e2e_taxonomy/opportunities/?diagram_id=${diagramId}`
  //     );

  //     const opportunitiesIDs = opportunitiesIDsResponse?.results.map((item: any) => item.id) ?? [];

  //     const completeSolutionData = await Promise.all(
  //       opportunitiesIDs.map(
  //         async (id: string) =>
  //           await fetchAISolutionWithAgentEnablement({
  //             solutionId: String(id),
  //             diagramId: diagramId,
  //             organizationId: currentOrg,
  //           })
  //       )
  //     );

  //     const validSolutionData = completeSolutionData.filter(
  //       (item): item is NonNullable<(typeof completeSolutionData)[number]> => item != null
  //     );

  //     validSolutionData.forEach((item: any) => {
  //       const custom = item?.agent?.ai_enablement?.enablement_matrix?.custom ?? [];
  //       const embedded = item?.agent?.ai_enablement?.enablement_matrix?.embedded ?? [];
  //       const extended = item?.agent?.ai_enablement?.enablement_matrix?.extended ?? [];

  //       item.agent_data = {
  //         custom,
  //         embedded,
  //         extended,
  //       };

  //       if (item.implementation_complexity.recommendation) {
  //         item.recommended = '(Recommended)';
  //       } else {
  //         item.recommended = '';
  //       }

  //       const supports_performance_improvement =
  //         item.performance_improvement.supports_performance_improvement.map(
  //           (performance: string) => {
  //             return performance.replace('#', 'Improvent ');
  //           }
  //         );
  //       item.performance_improvement.supports_performance_improvement =
  //         supports_performance_improvement;
  //     });

  //     // Get diagram XML from diagramData
  //     const diagramXML =
  //       (diagramData as any)?.diagram?.xml || (diagramData as any)?.DiagramData?.diagram?.xml || '';

  //     let diagram_image_base64 = '';
  //     if (diagramXML) {
  //       try {
  //         diagram_image_base64 = await convertBpmnXmlToBase64(diagramXML, 8);
  //       } catch (conversionError) {
  //         console.error('Failed to convert BPMN XML to base64 image:', conversionError);
  //       }
  //     }

  //     // Build deep-link URL for the diagram (encoded diagramId and processId).
  //     const processId = selectedCard?.id ?? '';
  //     let diagramUrl = '';
  //     if (diagramId) {
  //       const origin = window.location.origin;
  //       const query = [
  //         `diagramId=${encodeURIComponent(diagramId)}`,
  //         ...(processId ? [`processId=${encodeURIComponent(processId)}`] : []),
  //       ].join('&');
  //       diagramUrl = `${origin}/diagram?${query}`;
  //     }

  //     let modifiedSubTitle =
  //       (diagramData as any)?.useCaseData?.Opportunity_name ||
  //       selectedCard?.name ||
  //       'Detailed Opportunity Report';

  //     const opportunityName = (diagramData as any)?.useCaseData?.Opportunity_name;
  //     if (typeof opportunityName === 'string') {
  //       const regex = /^([A-Za-z]{3}-\d+):\s*(.*)$/;
  //       const match = opportunityName.match(regex);
  //       if (match && match[2]) {
  //         modifiedSubTitle = match[2];
  //         if (modifiedSubTitle.length > 0) {
  //           modifiedSubTitle = modifiedSubTitle[0].toUpperCase() + modifiedSubTitle.slice(1);
  //         }
  //       }
  //     }

  //     const payload: Record<string, unknown> = {
  //       subtitle: modifiedSubTitle,
  //       proposedEnhancements,
  //       validSolutionData,
  //       diagramXML: diagram_image_base64,
  //       diagramUrl,
  //       link: {
  //         text: 'View Diagram',
  //         url: diagramUrl,
  //       },
  //     };

  //     return payload;
  //   } catch (error) {
  //     console.error('Error processing report data:', error);
  //     throw error;
  //   }
  // };

  const fetchReportFromBackend = async (
    diagramId: string,
    caseId: string
  ): Promise<{
    detail: string;
    file_name: string;
    content_type: string;
    file_data_base64: string;
  }> => {
    try {
      const response = await httpService<any>(
        httpConstants.METHOD_TYPE.GET,
        `${API_BASE}/api/v1/e2e_taxonomy/opportunities/${diagramId}/report/?use_case_id=${caseId}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching report from backend:', error);
      throw error;
    }
  };

  const downloadFileFromBase64 = (base64Data: string, fileName: string, contentType: string) => {
    const binaryStr = atob(base64Data);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: contentType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadReport = useCallback(async () => {
    setLoadingDocument(true);
    setDownloadProgressOpen(true);
    setDownloadProgress(0);

    try {
      const selectedCardUseCaseId =
        selectedCard?.useCaseId || selectedCard?.use_case_id || selectedCard?.id;
      const caseIdToUse: string | undefined =
        typeof selectedCardUseCaseId === 'string' ? selectedCardUseCaseId : useCaseId;

      if (!caseIdToUse || typeof caseIdToUse !== 'string') {
        ShowToast({ message: 'Use case ID not found', type: ToastTypeEnum.Error });
        return;
      }

      const diagramId =
        (diagramData as any)?.diagram?.id ||
        (diagramData as any)?.DiagramData?.diagram?.id ||
        actualDiagramId;

      if (!diagramId) {
        ShowToast({ message: 'Diagram ID not found', type: ToastTypeEnum.Error });
        return;
      }

      setDownloadProgress(30);

      const reportResponse = await fetchReportFromBackend(diagramId, caseIdToUse);

      setDownloadProgress(60);

      downloadFileFromBase64(
        reportResponse.file_data_base64,
        reportResponse.file_name,
        reportResponse.content_type
      );

      setDownloadProgress(100);

      ShowToast({ message: 'Report downloaded successfully', type: ToastTypeEnum.Success });

      setTimeout(() => {
        setDownloadProgressOpen(false);
        setDownloadProgress(0);
      }, 1500);
    } catch (err) {
      console.error('Word document download failed', err);
      setDownloadProgressOpen(false);
      setDownloadProgress(0);
      ShowToast({ message: 'Failed to download report', type: ToastTypeEnum.Error });
    } finally {
      setLoadingDocument(false);
    }
  }, [
    actualDiagramId,
    diagramData,
    selectedCard?.id,
    selectedCard?.useCaseId,
    selectedCard?.use_case_id,
    useCaseId,
  ]);

  const hasActiveFilters = hasAnyFiltersApplied();
  const isResetMode = hasActiveFilters && !hasAnyTempFiltersSelected();

  useEffect(() => {
    if (!onMobileActionsChange) return;

    onMobileActionsChange({
      hasActiveFilters,
      showMenu: !isProcess,
      disableDownload: loadingDocument,
      onFilterClick: handleOpenFilterDrawer,
      onEditClick: handleRenameProcess,
      onDeleteClick: handleDeleteIdeation,
      onDownloadClick: handleDownloadReport,
    });
  }, [
    hasActiveFilters,
    handleDeleteIdeation,
    handleDownloadReport,
    handleOpenFilterDrawer,
    handleRenameProcess,
    isProcess,
    loadingDocument,
    onMobileActionsChange,
  ]);

  useEffect(() => {
    if (!onMobileActionsChange) return;

    return () => {
      onMobileActionsChange(null);
    };
  }, [onMobileActionsChange]);

  return (
    <Box
      sx={{
        minHeight: '0',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        overflowY: 'auto',
        backgroundColor: '#FFFFFF',
        pb: 14,
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: { xs: 'stretch', sm: 'start' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 0 },
          mb: 3,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="h6" sx={{ fontSize: { xs: '20px', sm: '24px' }, fontWeight: 500 }}>
              {isVersionViewMode && previewVersionData?.level_2?.name
                ? previewVersionData.level_2.name
                : currentProcessName || selectedProcess || 'AI Opportunities'} ({solutionData.length})
            </Typography>
            {isProcess ? (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#666666',
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {selectedCard?.name}
              </Typography>
            ) : (
              (selectedCard?.taxonomy || formState?.taxonomy) &&
              [
                selectedCard?.taxonomy?.domain || formState?.domain,
                selectedCard?.taxonomy?.function || formState?.selectedFunction,
                selectedCard?.taxonomy?.process_group || formState?.selectedProcessGroup,
                selectedCard?.taxonomy?.process || formState?.selectedProcess,
              ].filter(Boolean).length > 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#666666',
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {buildOpportunityBreadcrumbText({
                    taxonomy: selectedCard?.taxonomy,
                    fallbackDomain: formState?.domain,
                    fallbackFunction: formState?.selectedFunction,
                    fallbackProcessGroup: formState?.selectedProcessGroup,
                    fallbackProcess: formState?.selectedProcess,
                    currentProcessName,
                  })}
                </Typography>
              )
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: 1,
            justifyContent: { xs: 'flex-end', sm: 'flex-start' },
          }}
        >
          {/* Subtle filter icon placed next to title (doesn't change layout) */}
          <IconButton
            size="small"
            onClick={handleOpenFilterDrawer}
            sx={{
              border: '1px solid transparent',
              '&:hover': { backgroundColor: 'transparent' },
              ml: 0.5,
              position: 'relative',
            }}
            title="Sort / Filters"
          >
            {hasAnyFiltersApplied() && (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    width: 8,
                    height: 8,
                    backgroundColor: 'red',
                    borderRadius: '50%',
                  }}
                />
              </>
            )}
            <img src={'/images/ideation-filter.svg'} alt={'filter'} height={26} width={26} />
          </IconButton>

          {/* Filter Menu */}

          {/* Filter Drawer */}
          <Drawer
            open={showFilterDrawer}
            onClose={handleCloseFilterDrawer}
            anchor="right"
            hideBackdrop
            sx={{
              '& .MuiDrawer-paper': {
                width: {
                  xs: '90vw',
                  sm: '360px',
                },
                minWidth: '300px',
                maxWidth: '400px',

                p: { xs: 0, sm: 1 },

                marginTop: { xs: '60px', sm: '60px' },
                height: { xs: 'calc(100vh - 50px)', sm: 'calc(100vh - 60px)' },

                marginRight: { xs: 0, sm: '0px' },
                marginBottom: { xs: 0, sm: '0px' },

                borderTopLeftRadius: { xs: '16px', sm: 0 },
                borderTopRightRadius: { xs: '16px', sm: 0 },

                overflow: 'hidden',
                boxSizing: 'border-box',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflowX: 'hidden',
                px: { xs: '24px', sm: 0 },
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Typography sx={{ fontFamily: 'Poppins', fontSize: '18px', fontWeight: 600 }}>
                  Filter
                </Typography>
                <CloseIcon
                  onClick={handleCloseFilterDrawer}
                  sx={{
                    cursor: 'pointer',
                    fontSize: '20px',
                    '&:hover': { opacity: 0.7 },
                  }}
                />
              </Box>

              {/* Filter Sections */}
              <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pr: { xs: 0, sm: 0 } }}>
                {/* ROI Filter */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      fontFamily: 'Poppins',
                      mb: 1.5,
                      color: '#131426',
                    }}
                  >
                    ROI
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {['High', 'Medium', 'Low'].map((option) => (
                      <Box
                        key={option}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            filterValues.roi[
                              option.toLowerCase() as keyof typeof filterValues.roi as
                                | 'high'
                                | 'medium'
                                | 'low'
                            ]
                          }
                          onChange={() => handleFilterChange('roi', option)}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#1A55F9',
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '16px',
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            fontStyle: 'Regular',
                            color: '#000000',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {option}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Complexity Filter */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      fontFamily: 'Poppins',
                      mb: 1.5,
                      color: '#131426',
                    }}
                  >
                    Complexity
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {['High', 'Medium', 'Low'].map((option) => (
                      <Box
                        key={option}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            filterValues.complexity[
                              option.toLowerCase() as keyof typeof filterValues.complexity as
                                | 'high'
                                | 'medium'
                                | 'low'
                            ]
                          }
                          onChange={() => handleFilterChange('complexity', option)}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#1A55F9',
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '16px',
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            fontStyle: 'Regular',
                            color: '#000000',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {option}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Type Filter */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      fontFamily: 'Poppins',
                      mb: 1.5,
                      color: '#131426',
                    }}
                  >
                    Impact Type
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {['Transformative', 'Breakthrough', 'Incremental'].map((option) => (
                      <Box
                        key={option}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            filterValues.type[
                              option.toLowerCase() as keyof typeof filterValues.type as
                                | 'transformative'
                                | 'breakthrough'
                                | 'incremental'
                            ]
                          }
                          onChange={() => handleFilterChange('type', option)}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#1A55F9',
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '16px',
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            fontStyle: 'Regular',
                            color: '#000000',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {option}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Footer Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  pt: 2,
                  mt: 'auto',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleClearAll}
                  disabled={!hasAnyTempFiltersSelected() && !isResetMode}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    borderColor: '#E0E0E0',
                    color: '#1A55F9',
                    borderRadius: '6px',
                    py: 1.5,
                    fontWeight: 500,
                    '&:hover': {
                      borderColor: '#1A55F9',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  {isResetMode ? 'Reset' : 'Clear all'}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAddFilter}
                  disabled={!hasAnyTempFiltersSelected()}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    bgcolor: '#2054F4',
                    borderRadius: '6px',
                    py: 1.5,
                    fontWeight: 500,
                    '&:hover': { bgcolor: '#1A45C6' },
                  }}
                >
                  Apply filter
                </Button>
              </Box>
            </Box>
          </Drawer>
          {!isProcess && (
            <IdeationMenu
              onEdit={handleRenameProcess}
              onDelete={handleDeleteIdeation}
              onDownload={handleDownloadReport}
              onVersionHistory={onVersionHistory}
              isVersionViewMode={isVersionViewMode}
              editLabel="Rename Process"
              deleteLabel="Delete Ideation"
              disableDownload={loadingDocument}
            />
          )}

          {/* <CloseIcon
            onClick={() => setActiveStep?.('dashboard')}
            sx={{
              cursor: 'pointer',
              transition: '0.2s ease',
              padding: '1px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: '6px',
              },
            }}
          /> */}
        </Box>
      </Box>
      {/* Grid Container for 2 Column Layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr',
            md: 'repeat(auto-fill, minmax(500px, 1fr))',
          },
          gap: { xs: 1.5, sm: 2.5 },
          mb: { xs: 2.5, sm: 4 },
          width: '100%',
          paddingRight: { xs: '40px', sm: 'initial' },
          paddingLeft: { xs: '0px', sm: 'initial' },
        }}
      >
        {solutionData.map((solution) => (
          <Box key={solution.id} sx={{ display: 'flex', height: '100%' }}>
            <IdeationOpportunitiesCard
              title={solution.opportunity_name}
              tag={solution.impact || undefined}
              description={solution.description || 'No description available'}
              sequential_id={solution?.sequential_id || ''}
              complexity={
                typeof solution.complexity === 'string'
                  ? ((solution.complexity.charAt(0).toUpperCase() +
                      solution.complexity.slice(1).toLowerCase()) as 'High' | 'Medium' | 'Low')
                  : 'Medium'
              }
              roi={
                typeof solution.roi === 'string'
                  ? ((solution.roi.charAt(0).toUpperCase() +
                      solution.roi.slice(1).toLowerCase()) as 'High' | 'Medium' | 'Low')
                  : 'Medium'
              }
              approvalStatus={
                (solution.approval_status === 'pending'
                  ? 'approval_pending'
                  : solution.approval_status) as 'approved' | 'approval_pending' | 'created' | null
              }
              tagBgColor="#60A5FA"
              tagColor="#FFFFFF"
              vault={solution.vault}
              onClick={() => {
                setSelectedSolutionFromLibrary({
                  id: solution.id,
                  name: solution.opportunity_name,
                  vault: solution.vault,
                  solution_enabler: solution.solution_enabler || undefined,
                });
                setAiSolutionData({
                  ...solution,
                  id: solution.id,
                  opportunity_name: solution.opportunity_name,
                  roi: solution.roi,
                  impact: solution.impact,
                  complexity: solution.complexity,
                  description: solution.description,
                  roi_description: (solution as any).roi_description || '',
                  complexity_description: (solution as any).complexity_description || '',
                  benefits: (solution as any).benefits || {},
                  data_sources: (solution as any).data_sources || {
                    internal: { structured: [], unstructured: [] },
                    external: { structured: [], unstructured: [] },
                  },
                  key_requirements: (solution as any).key_requirements || [],
                  llm_agent: (solution as any).llm_agent || [],
                  solution_enabler: solution.solution_enabler,
                  diagram_id: actualDiagramId,
                  organization_id: currentOrg,
                  status: solution.approval_status || null,
                });
                setDataSourceType('rest');
              }}
              clickable={true}
              recommended={solution?.implementation_complexity?.recommendation}
            />
          </Box>
        ))}
      </Box>
      {/* Empty State */}
      {solutionData.length === 0 && !combinedLoading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            gap: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            No AI Opportunities Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI opportunities will appear here once they are generated
          </Typography>
        </Box>
      )}
      {/* Loading State */}
      {combinedLoading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '700px',
          }}
        >
          <Loader message="Loading AI Opportunities..." fullScreen={false} />
        </Box>
      )}

      {/* Rest of your modals and drawer code remains the same */}
      <Drawer
        open={!!selectedSolutionFromLibrary.id}
        onClose={async () => {
          setSelectedSolutionFromLibrary({ id: '', name: '' });
          if (needsRefetchOnClose) {
            setNeedsRefetchOnClose(false);
            try {
              const response = await fetchAISolution();
              if (response?.results?.length > 0) {
                const mapped = response.results.map(
                  (
                    {
                      id,
                      opportunity_name,
                      roi,
                      complexity,
                      impact,
                      vault,
                      solution_enabler,
                      status,
                      description,
                    }: any,
                    index: number
                  ) => ({
                    id,
                    serial: index + 1,
                    opportunity_name,
                    roi: roi || '-',
                    complexity: complexity || '-',
                    impact: impact || '-',
                    vault: vault || false,
                    solution_enabler: solution_enabler || 'N/A',
                    approval_status: status || null,
                    description: description || null,
                  })
                );
                sortSolutions(mapped);
                setDataSourceType('rest');
              } else if (data?.aiSolution?.fetch?.length) {
                const mapped = data.aiSolution.fetch.map(
                  ({ id, name, impactAssessment }: any, index: number) => ({
                    id,
                    serial: index + 1,
                    opportunity_name: name,
                    impact: impactAssessment?.general ?? 'N/A',
                  })
                );
                sortSolutions(mapped as AiSolutionRow[]);
                setDataSourceType('graphql');
              } else {
                setSolutionData([]);
                setDataSourceType(null);
              }
            } catch (err) {
              console.error('Error refetching solutions on close:', err);
            }
          }
        }}
        anchor="right"
        hideBackdrop
        ModalProps={{
          keepMounted: true,
          sx: {
            zIndex: 1400,
            '& .MuiDrawer-paper': {
              width: { xs: '100%', md: '70%' },
              pointerEvents: 'auto',
              marginTop: { xs: '56px', sm: 0 },
              height: { xs: 'calc(100vh - 56px)', sm: '100vh' },
              borderTopLeftRadius: { xs: '16px', sm: 0 },
              borderTopRightRadius: { xs: '16px', sm: 0 },
              borderTop: { xs: '2px solid #E0E0E0', sm: 'none' },
            },
          },
        }}
      >
        <Box>
          {dataSourceType === 'rest' && (
            <ViewSolutionDetails
              selectedSolutionId={selectedSolutionFromLibrary.id}
              selectedSolutionFromLibrary={selectedSolutionFromLibrary}
              enterpriseContextApplied={enterpriseContextApplied}
              setEnterpriseContextApplied={setEnterpriseContextApplied}
              aiSolutionData={aiSolutionData}
              setAiSolutionData={setAiSolutionData}
              diagramId={actualDiagramId}
              handleToggleVault={handleToggleVault}
              handleUpdateSolution={handleUpdateSolution}
              handleSave={handleSave}
              isProcessDiagramActive={true}
              handleRemoveClick={handleRemoveClick}
              onApproval={(updated: any) => {
                handleUpdateSolution(updated);
                setNeedsRefetchOnClose(true);
              }}
              closeDrawer={() => setSelectedSolutionFromLibrary({ id: '', name: '' })}
              isIdeation={isProcess ? false : true}
              processType={'custom'}
              isVersionViewMode={isVersionViewMode}
              onRestore={onRestoreVersion}
            />
          )}
          {dataSourceType === 'graphql' && (
            <ViewSolutionDetails
              selectedSolutionId={selectedSolutionFromLibrary.id}
              selectedSolutionFromLibrary={selectedSolutionFromLibrary}
              enterpriseContextApplied={enterpriseContextApplied}
              setEnterpriseContextApplied={setEnterpriseContextApplied}
              aiSolutionData={aiSolutionData}
              setAiSolutionData={setAiSolutionData}
              handleToggleVault={handleToggleVault}
              handleUpdateSolution={handleUpdateSolution}
              handleSave={handleSave}
              isProcessDiagramActive={true}
              handleRemoveClick={handleRemoveClick}
              onApproval={(updated: any) => {
                handleUpdateSolution(updated);
                setNeedsRefetchOnClose(true);
              }}
              closeDrawer={() => setSelectedSolutionFromLibrary({ id: '', name: '' })}
              isIdeation={isProcess ? false : true}
              isVersionViewMode={isVersionViewMode}
              onRestore={onRestoreVersion}
            />
          )}
        </Box>
      </Drawer>

      <CustomModal
        isOpen={showVaultModal}
        onClose={handleCloseVaultModal}
        title={selectedSolutionFromLibrary?.vault ? 'Remove from My List' : 'Add to My List'}
        maxWidth="sm"
      >
        <Box
          sx={{
            bgcolor: '#F9FBFF',
            borderRadius: '12px',
            p: 3,
            display: 'flex',
            alignItems: 'end',
            justifyContent: 'stretch',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '16px', color: '#2c2c2c' }}>
              Are you sure you want to{' '}
              {selectedSolutionFromLibrary?.vault
                ? 'remove this AI Solution from'
                : 'add this AI Solution to'}{' '}
              your list?
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleToggleVault}
            sx={{
              textTransform: 'none',
              bgcolor: '#2054F4',
              borderRadius: '6px',
              px: 3,
              py: 1,
              mt: 2,
              '&:hover': { bgcolor: '#1A45C6' },
              fontWeight: 'bold',
            }}
          >
            {selectedSolutionFromLibrary?.vault ? 'Remove' : 'Add'}
          </Button>
        </Box>
      </CustomModal>

      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        sx={{ zIndex: 1600, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box
          sx={{
            width: '400px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: 'auto',
            backgroundColor: 'white',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Typography sx={{ textAlign: 'center' }}>
            Are you sure you want to delete this solution?
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Button variant="contained" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: '#EF4444', '&:hover': { backgroundColor: '#EF4444' } }}
              onClick={() => deleteSolution(solutionToDelete)}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
      {/* Rename Process Modal */}
      <CustomModal
        isOpen={showEditConfirmModal}
        onClose={() => setShowEditConfirmModal(false)}
        title="Rename Process"
        maxWidth="sm"
        width="100%"
      >
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography sx={{ fontSize: '14px', color: '#2c2c2c', mb: 1.5, fontWeight: 500 }}>
              Process Name
            </Typography>
            <input
              type="text"
              value={renameProcessName}
              onChange={(e) => setRenameProcessName(e.target.value)}
              placeholder="Enter process name"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '16px',
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Poppins, sans-serif',
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'border-color 0.3s',
              }}
              onFocus={(e) => {
                (e.target as HTMLInputElement).style.borderColor = '#1A55F9';
              }}
              onBlur={(e) => {
                (e.target as HTMLInputElement).style.borderColor = '#E0E0E0';
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => {
                setShowEditConfirmModal(false);
                handleUpdateRenameProcess(renameProcessName);
              }}
              sx={{
                textTransform: 'none',
                bgcolor: '#2054F4',
                borderRadius: '6px',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                fontSize: '14px',
                '&:hover': { bgcolor: '#1A45C6' },
              }}
            >
              Update
            </Button>
          </Box>
        </Box>
      </CustomModal>

      <ConfirmationModal
        isOpen={showDeleteConfirmModal}
        title={`Delete Ideation`}
        description={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography sx={{ color: '#4f4f4f' }}>
              <Box>
                This will permanently delete the Ideation and all {solutionData.length}{' '}
                opportunities. This cannot be undone.
              </Box>
            </Typography>
          </Box>
        }
        primaryButtonText={
          <Box display="flex" alignItems="center" gap={1}>
            Delete
          </Box>
        }
        onPrimaryAction={handleConfirmDeleteIdeation}
        onClose={() => setShowDeleteConfirmModal(false)}
        width={'781px'}
        outerPadding={0}
      />
      <CustomModal
        isOpen={blockDeleteModalOpen}
        onClose={() => setBlockDeleteModalOpen(false)}
        title="Delete Ideation"
        maxWidth="sm"
        width="100%"
      >
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.6 }}>
            This Ideation has{' '}
            <strong>
              {
                solutionData.filter((s) => s.approval_status && s.approval_status !== 'created')
                  .length
              }
            </strong>{' '}
            opportunities active on COI Dashboard and/or Solution XPLR. Remove all child
            opportunities from downstream modules before deleting this Ideation.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setBlockDeleteModalOpen(false)}
              sx={{ textTransform: 'none', bgcolor: '#2054F4', '&:hover': { bgcolor: '#1A45C6' } }}
            >
              OK
            </Button>
          </Box>
        </Box>
      </CustomModal>
      <DownloadProgressSnackbar open={downloadProgressOpen} progress={downloadProgress} />
    </Box>
  );
};

export default AiOpportunities;
